import { useCallback, useEffect, useRef, useState } from "react";
import { IProduto } from "shared/interfaces/IProduto";

export type BuscarPaginaProdutos = (pagina: number) => Promise<{
  produtos: IProduto[];
  lastPage: number;
}>;

/**
 * Lista paginada com rolagem infinita: carrega a página 1, e as seguintes
 * quando o elemento de `sentinelaRef` se aproxima da tela.
 *
 * Mudar `chave` zera a lista e recomeça da página 1 (ex.: trocar a
 * categoria). Respostas de uma chave anterior que chegarem atrasadas são
 * descartadas.
 */
export function useProdutosInfinitos(
  buscarPagina: BuscarPaginaProdutos,
  chave: string,
) {
  const [produtos, setProdutos] = useState<IProduto[]>([]);
  const [pagina, setPagina] = useState(0);
  const [temMais, setTemMais] = useState(true);
  const [carregando, setCarregando] = useState(false);

  // Estado (e não useRef) para o observer ser criado quando a sentinela
  // aparecer na tela, mesmo que isso aconteça depois da página 1 carregar.
  const [sentinela, sentinelaRef] = useState<HTMLDivElement | null>(null);
  const buscarRef = useRef(buscarPagina);
  const geracaoRef = useRef(0);
  const carregandoRef = useRef(false);

  buscarRef.current = buscarPagina;

  const carregarPagina = useCallback(async (proximaPagina: number) => {
    if (carregandoRef.current) {
      return;
    }

    const geracao = geracaoRef.current;

    carregandoRef.current = true;
    setCarregando(true);

    try {
      const resultado = await buscarRef.current(proximaPagina);

      if (geracao !== geracaoRef.current) {
        return;
      }

      setProdutos((atuais) => {
        const idsExistentes = new Set(atuais.map((produto) => produto.id));

        return [
          ...atuais,
          ...resultado.produtos.filter(
            (produto) => !idsExistentes.has(produto.id),
          ),
        ];
      });
      setPagina(proximaPagina);
      setTemMais(proximaPagina < resultado.lastPage);
    } catch (error) {
      if (geracao === geracaoRef.current) {
        setTemMais(false);
      }
    } finally {
      if (geracao === geracaoRef.current) {
        carregandoRef.current = false;
        setCarregando(false);
      }
    }
  }, []);

  useEffect(() => {
    geracaoRef.current += 1;
    carregandoRef.current = false;

    setProdutos([]);
    setPagina(0);
    setTemMais(true);

    carregarPagina(1);
  }, [chave, carregarPagina]);

  useEffect(() => {
    if (!sentinela || pagina === 0 || !temMais) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          carregarPagina(pagina + 1);
        }
      },
      {
        root: null,
        rootMargin: "500px 0px",
        threshold: 0,
      },
    );

    observer.observe(sentinela);

    return () => {
      observer.disconnect();
    };
  }, [sentinela, pagina, temMais, carregarPagina]);

  return {
    produtos,
    temMais,
    carregando,
    carregandoInicial: carregando && pagina === 0,
    sentinelaRef,
  };
}
